import React from "react";
import ReactApexChart from "react-apexcharts";
import { Button, Checkbox, CheckboxGroup, CheckPicker, CheckTreePicker, FormGroup, Notification, Modal, List } from "rsuite";
import axios from "../../../../utilities/axios";
import { EmployeeErrorColumnChart } from "../../Types/EmployeeErrorColumnChartType";
import styles from "./styles/employeeErrorPerWeekChart.module.scss";
import { YearNode } from "../../Types/YearNodeType";
import dayjs from "dayjs";
import { CheckPickerSelectFilter } from "../../../../components/formfields/CheckPickerSelectFilter";

const chartFilterOptions: string[] = ['1', '2', '3', '4']

interface EmployeeErrorPerWeekChartProps {
    employee: { value: number, label: String }[]
    errors: { value: number, label: String; hasValue: boolean; }[]
}
interface EmployeeErrorPerWeekChartState {
    errors: { label: string; value: number, count: number }[];
    data: { count: number, value: number }[][];
    errorPerWeek: EmployeeErrorColumnChart;
    selectedEmployees: string[];
    selectedErrors: string[];
    selectedYear: string[];
    loadingButton: boolean;
    selectFullData: YearNode[];
    weekSelect: {
        indeterminate: boolean,
        checkAll: boolean,
        value: string[],
    },
    modalOpen: boolean;
    modalData: { weekLabel: string; errors: { errorName: string; count: number }[] } | null;
    detailsByWeek: Record<string, { errorName: string; count: number }[]>;

}

class EmployeeErrorPerWeekChart extends React.Component<EmployeeErrorPerWeekChartProps, EmployeeErrorPerWeekChartState> {
    public state: EmployeeErrorPerWeekChartState = {
        errors: [],
        data: [],
        modalOpen: false,
        modalData: null,
        detailsByWeek: {},
        errorPerWeek: {
            series: [
                {
                    data: [],
                    name: "Mensal",
                },
            ],
            options: {
                chart: {
                    type: "bar",
                    height: 350,
                    toolbar: {
                        show: true,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                plotOptions: {
                    bar: {
                        borderRadius: 2,
                        horizontal: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories: ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4'],
                },
                tooltip: {
                    y: {
                        formatter: (val, opts) => {
                            return `${val}`;
                        },
                    }
                }
            },
        },
        selectedEmployees: [],
        selectedErrors: [],
        selectedYear: ['2024'],
        loadingButton: false,
        selectFullData: [],
        weekSelect: {
            indeterminate: true,
            checkAll: true,
            value: ['1', '2', '3', '4'],
        }
    }

    async componentDidMount() {
        this.generateData()
    }

    generateData = (): void => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const startYear = 2024;

        type MonthNode = { label: string; value: string };
        type YearNode = { label: string; value: string; children: MonthNode[] };

        const data: YearNode[] = [];

        for (let year = startYear; year <= currentYear; year++) {
            const yearNode: YearNode = {
                label: `${year}`,
                value: `${year}`,
                children: [],
            };

            const monthsInYear = year === currentYear ? currentMonth : 12;
            for (let month = 1; month <= monthsInYear; month++) {
                const monthLabel = new Date(year, month - 1).toLocaleString("default", {
                    month: "long",
                });

                yearNode.children.push({
                    label: monthLabel,
                    value: `${year}-${month.toString().padStart(2, "0")}`,
                });
            }

            data.push(yearNode);
        }

        this.setState({ selectFullData: data });
    };


    handleSetLoadingTrue = () => {
        this.setState({ loadingButton: true });
    };

    handleSetLoadingFalse = () => {
        this.setState({ loadingButton: false });
    };

    handleGetChartData = async () => {
        this.handleSetLoadingTrue();

        try {
            const { weekSelect, selectedEmployees, selectedYear, selectedErrors } = this.state;

            if (!selectedYear || selectedYear.length === 0) {
                Notification.warning({
                    title: "Aviso!",
                    description: "Selecione um ano para gerar o gráfico.",
                    duration: 3500,
                });

                this.handleSetLoadingFalse();
                return;
            }

            const sortedWeekSelect = weekSelect?.value.sort((a: any, b: any) => Number(a) - Number(b))

            const response = await axios.get("/api/v1/dashboard/get_linechart_information", {
                params: {
                    weekSelect: sortedWeekSelect ? sortedWeekSelect : [],
                    employee: selectedEmployees,
                    fullPeriod: selectedYear,
                    lineChartFilterType: "employee",
                    store: [],
                    platform: [],
                    errors: selectedErrors
                },
            })

            const { series, categories, detailsByWeek } = response.data;

            if (!Array.isArray(series) || series.length === 0 || !Array.isArray(categories)) {
                this.setState({
                    errorPerWeek: {
                        ...this.state.errorPerWeek,
                        series: [{ name: "Mensal", data: [] }],
                        options: {
                            ...this.state.errorPerWeek.options,
                            xaxis: {
                                ...this.state.errorPerWeek.options.xaxis,
                                categories: [],
                            },
                            noData: {
                                text: 'Nenhum dado encontrado',
                                align: 'center',
                                verticalAlign: 'middle',
                                style: {
                                    color: '#999',
                                    fontSize: '16px',
                                    fontFamily: 'sans-serif'
                                }
                            }
                        },
                    }
                });


                Notification.info({
                    title: "Sem dados",
                    description: "Nenhum dado foi encontrado para os filtros aplicados.",
                    duration: 3500,
                });

                return;
            }

            this.setState({
                detailsByWeek,
                errorPerWeek: {
                    ...this.state.errorPerWeek,
                    series: series,
                    options: {
                        ...this.state.errorPerWeek.options,
                        chart: {
                            ...this.state.errorPerWeek.options.chart,
                            events: {
                                dataPointSelection: (event: any, chartContext: any, config: any) => {
                                    const weekLabel = categories[config.dataPointIndex];
                                    const errors = this.state.detailsByWeek?.[weekLabel] ?? [];
                                    this.setState({ modalOpen: true, modalData: { weekLabel, errors } });
                                }
                            }
                        },
                        xaxis: {
                            ...this.state.errorPerWeek.options.xaxis,
                            categories,
                        },
                    },
                },
            });

        } catch (error) {
            console.log("Erro ao buscar informações para o gráfico de funcionário por semana ::::>> ", error)
            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar informações para o gráfico de funcionário por semana!",
                duration: 3500
            })
        } finally {
            this.handleSetLoadingFalse();
        }
    }

    handleCheckAll = (value: any, checked: any) => {
        const nextValue = checked ? chartFilterOptions : [];
        this.setState((prevState) => ({
            weekSelect: {
                value: nextValue,
                indeterminate: false,
                checkAll: checked,
            },
        }));
    };

    handleChange = (value: any) => {
        this.setState((prevState) => ({
            weekSelect: {
                value,
                indeterminate: value.length > 0 && value.length < chartFilterOptions.length,
                checkAll: value.length === chartFilterOptions.length,
            },
        }));
    };

    render() {

        const renderWeeks = () => {
            const today = dayjs()
            const currentMonday = today.clone().subtract(today.clone().day() == 0 ? 1 : 0, 'day').subtract(1, 'week').startOf('week').add(1, 'day');

            return [1, 2, 3, 4].map(week => (
                <Checkbox value={week.toString()}>
                    {currentMonday.clone().subtract(7 * (week - 1), 'day').startOf('day').format('DD/MM')}
                    &nbsp;-&nbsp;
                    {currentMonday.clone().subtract(7 * (week - 1), 'day').add(6, 'day').endOf('day').format('DD/MM')}
                </Checkbox>
            ))
        }

        return (
          <>
            <div className={styles.mainContainer}>
              <div className={styles.containerFilters}>
                <FormGroup style={{
                    width: "100%",
                  }}>
                  <span>Funcionários</span>
                  <CheckTreePicker
                    data={this.props.employee}
                    placeholder="Selecione funcionários..."
                    style={{ width: "100%" }}
                    value={this.state.selectedEmployees}
                    onChange={(value) => this.setState({ selectedEmployees: value })}
                  />
                </FormGroup>

                <FormGroup style={{
                    width: '100%'
                  }}>
                  <span>Lançamentos</span>
                  <CheckPickerSelectFilter
                    onSelectFilter={(value) => this.setState({ selectedErrors: value }) }
                    data={this.props.errors}
                    placeholder="Selecione Lançamentos..."
                    style={{ width: "100%" }}
                    value={this.state.selectedErrors}
                    onChange={(value) => this.setState({ selectedErrors: value }) }
                  />
                </FormGroup>

                <div className={styles.filterButtons}>
                  <span>ㅤ</span>
                  <Button
                    style={{ width: "250px" }}
                    appearance="primary"
                    color="blue"
                    loading={this.state.loadingButton}
                    onClick={this.handleGetChartData}
                  >
                    Filtrar
                  </Button>
                </div>
              </div>

              <div className={styles.chartContainer}>
                <div style={{ width: '100%' }}>
                  <div id="chart">
                    <ReactApexChart
                      options={this.state.errorPerWeek.options}
                      series={this.state.errorPerWeek.series}
                      type="bar"
                      height={350}
                    />
                  </div>
                  <div id="html-dist"></div>
                </div>

                <div className={styles.checkboxContainer}>
                  <Checkbox
                    indeterminate={this.state.weekSelect.indeterminate}
                    checked={this.state.weekSelect.checkAll}
                    onChange={this.handleCheckAll}
                  >
                    Marcar tudo
                  </Checkbox>
                  <CheckboxGroup
                    name="checkboxList"
                    value={this.state.weekSelect.value}
                    onChange={this.handleChange}
                  >
                    {renderWeeks()}
                  </CheckboxGroup>
                </div>
              </div>
            </div>
            <Modal
              backdrop={true}
              show={this.state.modalOpen}
              onHide={() => this.setState({ modalOpen: false })}
            >
              <Modal.Header>
                <Modal.Title>
                  Lançamentos — {this.state.modalData?.weekLabel}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <List>
                  {this.state.modalData?.errors.map(
                    (err: any, index: number) => (
                      <List.Item key={index} index={index}>
                        {err.errorName} — {err.count} ocorrência
                        {err.count > 1 ? "s" : ""}
                      </List.Item>
                    ),
                  )}
                </List>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  onClick={() => this.setState({ modalOpen: false })}
                  appearance="subtle"
                >
                  Fechar
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        );
    }
}

export default EmployeeErrorPerWeekChart;
